import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import type { ExtractionJob } from '../../types';

interface Session {
  complaintJobId: string;
  answerJobId?: string;
}

interface Props {
  matterId: string;
  onOpenAIExtract: () => void;
}

export function AIExtractionProgress({ matterId, onOpenAIExtract }: Props): React.JSX.Element | null {
  const [jobs, setJobs] = useState<ExtractionJob[]>([]);
  const [session, setSession] = useState<Session | null>(null);

  useEffect((): (() => void) | undefined => {
    if (!matterId) return;
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      let saved: Session | null = null;
      try {
        const raw = localStorage.getItem(`lattice.pleading-extraction.${matterId}`);
        if (raw) saved = JSON.parse(raw) as Session;
      } catch { /* Storage may be unavailable. */ }
      if (!saved?.complaintJobId) {
        if (!cancelled) { setSession(null); setJobs([]); }
        return;
      }
      try {
        const response = await apiFetch(`/api/v1/matters/${matterId}/extraction-jobs`);
        if (!response.ok) return;
        const all = await response.json<ExtractionJob[]>();
        const ids = new Set([saved.complaintJobId, saved.answerJobId].filter(Boolean));
        if (!cancelled) { setSession(saved); setJobs(all.filter(job => ids.has(job._key))); }
      } catch { /* Retry on the next poll. */ }
    };
    void refresh();
    const timer = window.setInterval((): undefined => void refresh(), 5000);
    return (): void => { cancelled = true; window.clearInterval(timer); };
  }, [matterId]);

  if (!session) return null;
  const total = session.answerJobId ? 2 : 1;
  const complete = jobs.filter(job => job.status === 'complete').length;
  const failed = jobs.find(job => job.status === 'failed');
  const settled = jobs.length === total && jobs.every(job => job.status === 'complete' || job.status === 'failed');
  const openReview = (): void => {
    try { localStorage.removeItem(`lattice.pleading-extraction.${matterId}`); } catch { /* Storage may be unavailable. */ }
    setSession(null);
    onOpenAIExtract();
  };

  if (settled) {
    return (
      <button type="button" className="ai-extraction-notice" onClick={openReview} aria-label="Open AI Extract">
        <span aria-hidden="true">🔔</span>
        {failed ? 'AI extraction needs attention' : 'AI extraction ready to review'}
        <span aria-hidden="true">→</span>
      </button>
    );
  }

  return (
    <button type="button" className="ai-extraction-progress" onClick={onOpenAIExtract} aria-label="View AI extraction progress">
      <span>AI extracting · {complete} of {total} complete</span>
      <span className="ai-extraction-track" role="progressbar" aria-label="AI extraction" aria-valuemin={0} aria-valuemax={total} aria-valuenow={complete}>
        <span className="ai-extraction-fill" style={{ width: `${Math.max(12, complete / total * 100)}%` }} />
      </span>
    </button>
  );
}
